import React, { useState, useEffect } from 'react';
import { X, Ruler, Check, ArrowLeftRight, ArrowUpDown } from 'lucide-react';
import { WindowElement } from '../types';

interface SolidBoxWindowMeasurementPromptProps {
  isOpen: boolean;
  windowElement: WindowElement | null;
  onConfirm: (width: number, height: number, depth: number) => void;
  onCancel: () => void;
}

const SolidBoxWindowMeasurementPrompt: React.FC<SolidBoxWindowMeasurementPromptProps> = ({
  isOpen,
  windowElement,
  onConfirm,
  onCancel
}) => {
  const [width, setWidth] = useState<number>(80);
  const [height, setHeight] = useState<number>(60);
  const [depth, setDepth] = useState<number>(40);

  useEffect(() => {
    if (windowElement) {
      setWidth(windowElement.width || 80);
      setHeight(windowElement.height || 60);
      setDepth(windowElement.depth || 40);
    }
  }, [windowElement]);

  if (!isOpen || !windowElement) return null;

  const handleConfirm = () => {
    onConfirm(width, height, depth);
  };

  const handleWidthChange = (value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0 && num <= 500) {
      setWidth(num);
    }
  };

  const handleHeightChange = (value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0 && num <= 500) {
      setHeight(num);
    }
  };

  const handleDepthChange = (value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0 && num <= 200) {
      setDepth(num);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-xl shadow-2xl p-6 w-[500px] max-w-[90vw] border border-slate-200/50">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Ruler className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Adjust Window Measurements</h3>
              <p className="text-sm text-slate-500">Set the dimensions for your solid box window</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Measurement Grid */}
        <div className="space-y-4 mb-6">
          {/* Width */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                  <ArrowLeftRight className="w-4 h-4 text-white" />
                </div>
                <label className="text-sm font-medium text-slate-700">Width</label>
              </div>
              <span className="text-xs text-slate-500">pixels</span>
            </div>
            <input
              type="number"
              value={width}
              onChange={(e) => handleWidthChange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-800"
              min="20"
              max="500"
              step="5"
            />
            <p className="text-xs text-slate-500 mt-1">Recommended: 60-150px</p>
          </div>

          {/* Height */}
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-gradient-to-r from-purple-500 to-violet-600 flex items-center justify-center shadow-md">
                  <ArrowUpDown className="w-4 h-4 text-white" />
                </div>
                <label className="text-sm font-medium text-slate-700">Height</label>
              </div>
              <span className="text-xs text-slate-500">pixels</span>
            </div>
            <input
              type="number"
              value={height}
              onChange={(e) => handleHeightChange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-slate-800"
              min="20"
              max="500"
              step="5"
            />
            <p className="text-xs text-slate-500 mt-1">Recommended: 40-120px</p>
          </div>

          {/* Depth */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-gradient-to-r from-orange-500 to-amber-600 flex items-center justify-center shadow-md">
                  <Ruler className="w-4 h-4 text-white" />
                </div>
                <label className="text-sm font-medium text-slate-700">Projection Depth</label>
              </div>
              <span className="text-xs text-slate-500">pixels</span>
            </div>
            <input
              type="number"
              value={depth}
              onChange={(e) => handleDepthChange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-slate-800"
              min="10"
              max="200"
              step="5"
            />
            <p className="text-xs text-slate-500 mt-1">How far the window extends from the wall</p>
          </div>
        </div>

        {/* Visual Preview */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 mb-6 border border-slate-200">
          <p className="text-xs font-medium text-slate-600 mb-2">Preview Dimensions:</p>
          <div className="flex items-center justify-around text-sm">
            <div className="text-center">
              <div className="text-blue-600 font-bold">{width}px</div>
              <div className="text-xs text-slate-500">Width</div>
            </div>
            <div className="text-slate-300">×</div>
            <div className="text-center">
              <div className="text-purple-600 font-bold">{height}px</div>
              <div className="text-xs text-slate-500">Height</div>
            </div>
            <div className="text-slate-300">×</div>
            <div className="text-center">
              <div className="text-orange-600 font-bold">{depth}px</div>
              <div className="text-xs text-slate-500">Depth</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] font-medium flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Confirm Measurements
          </button>
        </div>
      </div>
    </div>
  );
};

export default SolidBoxWindowMeasurementPrompt;
